import React, { useCallback, useEffect, useState } from "react";
import { FileCheck2, ExternalLink, Loader2, Wand2, CircleCheck, CircleDashed, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// V1.34 · Panel ALTA DGT
// Muestra el estado de los 3 documentos requeridos (DNI anverso, DNI reverso,
// Psicotécnico) y permite generar/regenerar el PDF combinado manualmente.
// El PDF también se genera automáticamente al validar el 3º documento
// requerido (auto-trigger en el backend).

const REQUIRED = [
  { key: "dni_anverso",  label: "DNI Anverso" },
  { key: "dni_reverso",  label: "DNI Reverso" },
  { key: "psicotecnico", label: "Psicotécnico" },
];

export default function AltaDgtPanel({ studentId }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  // V1.42.5 · Selección de documentos a incluir en el PDF final. Por defecto
  // los 3 requeridos (compatibilidad total con el flujo actual).
  const [selected, setSelected] = useState({
    dni_anverso: true, dni_reverso: true, psicotecnico: true,
  });

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/students/${studentId}/documents/alta-dgt/status`);
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  // V1.85 · Se recarga cuando se sube o valida un documento del alumno y al
  // volver a la pestaña, para que un psicotécnico tardío habilite el botón.
  useEffect(() => {
    const onChanged = (e) => {
      if (!e?.detail?.studentId || e.detail.studentId === studentId) load();
    };
    window.addEventListener("student-files-changed", onChanged);
    window.addEventListener("focus", load);
    return () => {
      window.removeEventListener("student-files-changed", onChanged);
      window.removeEventListener("focus", load);
    };
  }, [load, studentId]);

  const doGenerate = useCallback(async (opts) => {
    const auto = !!(opts && opts.auto);
    // V1.42.5 · Enviamos sólo los checks activos; el backend valida ≥1.
    const include = Object.keys(selected).filter((k) => selected[k]);
    if (include.length === 0) {
      toast.error("Selecciona al menos un documento para generar el PDF");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post(
        `/students/${studentId}/documents/generate-alta-dgt`,
        { include },
      );
      toast.success(`${auto ? "ALTA DGT regenerado" : "ALTA DGT generado"} (${Math.round((data.size_kb || 0))} KB)`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error al generar");
    } finally {
      setGenerating(false);
    }
  }, [studentId, selected, load]);

  if (loading) return null;
  if (!status) return null;

  // V1.42.5 · `ready` se calcula sobre los documentos SELECCIONADOS, no sobre
  // los 3 fijos. Así el botón se habilita si el subconjunto elegido está
  // completo (todos los seleccionados están presentes en `status.present`).
  const selectedKeys = Object.keys(selected).filter((k) => selected[k]);
  const anySelected = selectedKeys.length > 0;
  const selectedReady = anySelected && selectedKeys.every((k) => !!status.present?.[k]);
  const selectedMissing = selectedKeys.filter((k) => !status.present?.[k]);
  const ready = selectedReady;
  const alta = status.current_alta_dgt;

  return (
    <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50/60 p-3" data-testid="alta-dgt-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <FileCheck2 className="h-4 w-4 text-indigo-600 shrink-0" />
          <div className="min-w-0">
            <div className="text-xs font-bold text-neutral-900 flex items-center gap-1.5">
              PDF ALTA DGT
              <span className="text-[10px] font-mono text-neutral-500">V1.34</span>
              {alta && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-700">
                  <CircleCheck className="h-3 w-3" /> generado v{alta.version}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {REQUIRED.map((r) => {
                const p = status.present?.[r.key];
                const has = !!p;
                const v = p?.validation_status;
                const hasCrop = p && p.crop_config && p.crop_config.mode === "manual";
                const checked = !!selected[r.key];
                return (
                  <span
                    key={r.key}
                    className={
                      "inline-flex items-center gap-1 text-[10px] rounded-full pl-1 pr-0.5 py-0.5 border " +
                      (!checked
                        ? "bg-neutral-50 border-neutral-200 text-neutral-400 opacity-70"
                        : has
                        ? v === "validated"
                          ? "bg-emerald-100 border-emerald-300 text-emerald-800"
                          : "bg-amber-100 border-amber-300 text-amber-800"
                        : "bg-neutral-100 border-neutral-300 text-neutral-500")
                    }
                    data-testid={`alta-req-${r.key}`}
                  >
                    {/* V1.42.5 · Checkbox de inclusión en el PDF final */}
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => setSelected((s) => ({ ...s, [r.key]: e.target.checked }))}
                      className="h-3 w-3 accent-indigo-600 cursor-pointer"
                      title={checked ? "Incluir en el PDF ALTA DGT" : "Excluir del PDF ALTA DGT"}
                      data-testid={`alta-req-${r.key}-include`}
                    />
                    {has ? (
                      <CircleCheck className="h-2.5 w-2.5" />
                    ) : (
                      <CircleDashed className="h-2.5 w-2.5" />
                    )}
                    {r.label}
                    {hasCrop && (
                      <span
                        className="text-[9px] uppercase font-mono ml-0.5 px-1 rounded bg-indigo-600 text-white"
                        title="Recorte manual configurado (aplícalo en Cloud → botón Sustituir en OneDrive)"
                        data-testid={`alta-req-${r.key}-crop-badge`}
                      >crop</span>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={load}
            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-full border border-indigo-300 bg-white text-indigo-800 hover:bg-indigo-100 text-xs font-semibold transition-colors"
            title="Volver a comprobar los documentos subidos"
            data-testid="alta-dgt-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Actualizar
          </button>
          {alta?.onedrive_web_url && (
            <a
              href={alta.onedrive_web_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 h-8 px-3 rounded-full border border-indigo-300 bg-white text-indigo-800 hover:bg-indigo-100 text-xs font-semibold transition-colors"
              data-testid="alta-dgt-open"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Abrir PDF
            </a>
          )}
          <button
            onClick={() => doGenerate()}
            disabled={!ready || generating}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title={ready ? "Genera (o regenera) el PDF ALTA DGT" : "Faltan documentos requeridos"}
            data-testid="alta-dgt-generate-btn"
          >
            {generating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Wand2 className="h-3.5 w-3.5" />
            )}
            {generating ? "Generando…" : alta ? "Regenerar" : "Generar ALTA DGT"}
          </button>
        </div>
      </div>
      {!ready && anySelected && selectedMissing.length > 0 && (
        <div className="mt-2 text-[10px] text-neutral-500 italic">
          Faltan (de tu selección): {selectedMissing.join(", ")}.
        </div>
      )}
      {!anySelected && (
        <div className="mt-2 text-[10px] text-red-600 italic">
          Marca al menos un documento con el check para poder generar el PDF.
        </div>
      )}
      {alta && (
        <div className="mt-2 text-[10px] text-neutral-500">
          {alta.filename} · {new Date(alta.uploaded_at).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} · {Math.round((alta.size_bytes || 0) / 1024)} KB
        </div>
      )}
    </div>
  );
}

